import type { Model } from "@binale-tech/shared";
import { type BwaSchemaRow, isBwaSumRow } from "./bwaSchema";
import { BWA_SCHEMA_SKR03 } from "./bwaSchemaSkr03";
import { BWA_SCHEMA_SKR04 } from "./bwaSchemaSkr04";

export interface BwaAccountRow {
    key: number;
    konto?: Model.IExtNum;
    periodAmounts: Map<number, number>;
}

export interface BwaResultRow<T extends BwaAccountRow = BwaAccountRow> {
    /** DATEV BWA-Zeile number */
    key: number;
    labelId: string;
    /** DATEV Kennzeichen A-I of the Zwischensummen, null on account lines */
    kennz: string | null;
    kind: "group" | "subGroup";
    periodAmounts: Map<number, number>;
    accounts: T[];
}

const L = "app.overview.bwa.";

/** i18n label per DATEV BWA-Zeile; the Zwischensummen labels carry their Kennzeichen (A. Gesamtleistung ...) */
export const BWA_LABEL_IDS: Readonly<Record<number, string>> = {
    1020: `${L}umsatzerloese`,
    1040: `${L}bestandsveraenderung`,
    1045: `${L}aktivierteEigenleistungen`,
    1051: `${L}gesamtleistung`,
    1060: `${L}materialWareneinkauf`,
    1080: `${L}rohertrag`,
    1090: `${L}sonstigeBetrieblicheErloese`,
    1092: `${L}betrieblicherRohertrag`,
    1100: `${L}personalkosten`,
    1120: `${L}raumkosten`,
    1140: `${L}betrieblicheSteuern`,
    1150: `${L}versicherungenBeitraegeAbgaben`,
    1160: `${L}besondereKosten`,
    1180: `${L}fahrzeugkosten`,
    1200: `${L}werbeReisekosten`,
    1220: `${L}kostenWarenabgabe`,
    1240: `${L}abschreibungen`,
    1250: `${L}reparaturenInstandhaltungen`,
    1260: `${L}sonstigeKosten`,
    1280: `${L}gesamtkosten`,
    1300: `${L}betriebsergebnis`,
    1310: `${L}zinsaufwendungen`,
    1312: `${L}sonstigerNeutralerAufwand`,
    1320: `${L}neutralerAufwand`,
    1322: `${L}zinsertraege`,
    1323: `${L}sonstigerNeutralerErtrag`,
    1324: `${L}verrechneteKalkKosten`,
    1330: `${L}neutralerErtrag`,
    1340: `${L}kontenklasseUnbesetzt`,
    1345: `${L}ergebnisVorSteuern`,
    1355: `${L}steuernEinkommenErtrag`,
    1380: `${L}vorlaeufigesErgebnis`,
};

export const getBwaSchema = (skr: number): readonly BwaSchemaRow[] => (skr === 3 ? BWA_SCHEMA_SKR03 : BWA_SCHEMA_SKR04);

// keyed by Model.Period: 0 = FirstDayOfYear (Saldenvortrag), 1-12 = months, 13 = LastDayOfYear (Abschluss)
const emptyPeriodValues = new Map(new Array(14).fill(0).map((v, idx) => [idx, 0]));

const addInto = (target: Map<number, number>, source: Map<number, number>, factor: number) => {
    source.forEach((value, period) => {
        target.set(period, (target.get(period) ?? 0) + factor * value);
    });
};

const negateInPlace = (amounts: Map<number, number>) => {
    // `|| 0` normalizes -0, which Intl would otherwise render as "-0,00"
    amounts.forEach((value, period) => amounts.set(period, -value || 0));
};

/**
 * Pure BWA aggregation over per-account period saldi (S+/H−, SuSa convention).
 * Lines are computed DATEV-style (Ertrag Haben-positive, Aufwand Soll-positive, Zwischensummen per the
 * sheet's signed formulas), then Aufwand rows are negated: the app displays costs negative.
 * Copies period Maps before touching them: bwaData is a shared memoized source (H10).
 */
export const computeBwaGroups = <T extends BwaAccountRow>(bwaData: Map<string, T>, skr: number): BwaResultRow<T>[] => {
    const schema = getBwaSchema(skr);
    const catList: string[] = Array.from(bwaData.keys()).toSorted((a, b) => Number(a) - Number(b));
    const byKey = new Map<number, BwaResultRow<T>>();
    const result: BwaResultRow<T>[] = [];

    for (const def of schema) {
        const row: BwaResultRow<T> = {
            key: def.zeile,
            labelId: BWA_LABEL_IDS[def.zeile],
            kennz: def.kennz,
            kind: isBwaSumRow(def) ? "group" : "subGroup",
            periodAmounts: new Map(emptyPeriodValues),
            accounts: [],
        };
        if (isBwaSumRow(def)) {
            for (const { zeile, sign } of def.formula) {
                addInto(row.periodAmounts, byKey.get(zeile)!.periodAmounts, sign);
            }
        } else {
            for (const catNumStr of catList) {
                const catNum = Number(catNumStr.substring(0, 4));
                if (!def.ranges.some(([min, max]) => min <= catNum && catNum <= max)) {
                    continue;
                }
                const src = bwaData.get(catNumStr)!;
                const item = { ...src, periodAmounts: new Map(src.periodAmounts) };
                if (def.nature === "ertrag") {
                    negateInPlace(item.periodAmounts);
                }
                row.accounts.push(item);
                addInto(row.periodAmounts, item.periodAmounts, 1);
            }
        }
        byKey.set(def.zeile, row);
        result.push(row);
    }

    schema.forEach((def) => {
        if (def.nature === "aufwand") {
            const row = byKey.get(def.zeile)!;
            negateInPlace(row.periodAmounts);
            row.accounts.forEach((account) => negateInPlace(account.periodAmounts));
        }
    });
    return result;
};
